import * as moment from 'moment';
import type {
  OperatorStrategy,
  OperatorKey,
  SelectionBlockerArgs,
  SelectionBlockerResult,
  UnitCostCalculationArgs,
} from '../operator-strategy';

/**
 * Abstract base class for operator pricing strategies.
 * Encapsulates common logic: factor defaults, factor application, latency-index lookup.
 */
export abstract class BaseOperatorStrategy implements OperatorStrategy {
  abstract readonly operatorKey: OperatorKey;

  /** Returns the current UTC instant as a Date, consistent with the component's time baseline. */
  protected resolveSubmissionDate(): Date {
    return new Date(moment.utc().format('YYYY-MM-DD HH:mm:ss'));
  }

  /**
   * Checks sensorRow.poi / sensorRow.aoi flags against the current target area and returns
   * a blocker if the sensor does not support the selected target type.
   * - poi=true, aoi=false/absent → blocks when selectedAreaSqkm > 0 (AOI target)
   * - aoi=true, poi=false/absent → blocks when selectedAreaSqkm === 0 (POI target)
   * Returns null when no constraint is violated.
   */
  protected getAoiPoiFlagBlocker(
    args: SelectionBlockerArgs,
    code?: string,
  ): SelectionBlockerResult | null {
    const sensorRow = args?.sensorRow;
    const selectedAreaSqkm = args.taskingContext?.selectedAreaSqkm ?? 0;
    const poiOnly = sensorRow?.poi && !sensorRow?.aoi;
    const aoiOnly = sensorRow?.aoi && !sensorRow?.poi;

    if (poiOnly && selectedAreaSqkm > 0) {
      return this.createAoiNotSupportedBlocker(sensorRow, code ?? 'AOI_NOT_SUPPORTED');
    }
    if (aoiOnly && selectedAreaSqkm <= 0) {
      return this.createPoiNotSupportedBlocker(sensorRow, code ?? 'POI_NOT_SUPPORTED');
    }
    return null;
  }

  protected createAoiNotSupportedBlocker(
    sensorRow: any,
    code = 'AOI_NOT_SUPPORTED',
  ): SelectionBlockerResult {
    const sensorName = [sensorRow?.displayName, sensorRow?.sensor]
      .filter(Boolean)
      .join(' ')
      .trim() || 'This sensor';

    return {
      code,
      message: `${sensorName} only supports point of interest (POI) tasking. Please change your target to a point or choose an alternative provider.`,
    };
  }

  protected createPoiNotSupportedBlocker(
    sensorRow: any,
    code = 'POI_NOT_SUPPORTED',
  ): SelectionBlockerResult {
    const sensorName = [sensorRow?.displayName, sensorRow?.sensor]
      .filter(Boolean)
      .join(' ')
      .trim() || 'This sensor';

    return {
      code,
      message: `${sensorName} only supports area of interest (AOI) tasking. Please change your target to a polygon or choose an alternative provider.`,
    };
  }

  protected createUnsupportedRecurrencyBlocker(
    sensorRow: any,
    code = 'UNSUPPORTED_RECURRENCY',
  ): SelectionBlockerResult {
    const sensorName = [sensorRow?.displayName, sensorRow?.sensor]
      .filter(Boolean)
      .join(' ')
      .trim() || 'This sensor';

    return {
      code,
      message: `${sensorName} does not support recurrency at this time. Please change your mode of frequency or choose an alternative provider.`,
    };
  }

  syncLatencyCosts(_sensorRow: any): void {
    // Default: no-op. Subclasses may override when per-latency costs depend on other selections.
  }

  /**
   * Subclasses compute the base cost (before factors) and may sync per-latency unitcosts.
   * Return the base cost for the selected latency index, or null to signal early exit.
   */
  protected abstract computeBaseCost(sensorRow: any, latencyIndx: number): number | null;

  /**
   * Convenience hook: recompute using the row's currently-selected latency.
   * Looks up latency index from `selectedLatency`, falls back to `selected === true`, then index 0.
   */
  recalculateSelectedCost(sensorRow: any): void {
    if (!sensorRow || !Array.isArray(sensorRow.latency) || sensorRow.latency.length === 0) return;

    let latencyIndx = -1;
    if (sensorRow.selectedLatency) {
      latencyIndx = sensorRow.latency.findIndex((l: any) => l?.name === sensorRow.selectedLatency);
    }
    if (latencyIndx < 0) {
      latencyIndx = sensorRow.latency.findIndex((l: any) => l?.selected === true);
    }
    if (latencyIndx < 0) latencyIndx = 0;

    this.calculateUnitCost({ sensorRow, latencyIndx });
  }

  /**
   * Main entry: ensures factor defaults, delegates to subclass for base cost, then applies factors.
   */
  calculateUnitCost({ sensorRow, latencyIndx }: UnitCostCalculationArgs): void {
    if (!sensorRow || !Array.isArray(sensorRow.latency)) return;

    this.ensureFactorDefaults(sensorRow);

    const baseCost = this.computeBaseCost(sensorRow, latencyIndx);
    if (baseCost == null) return; // subclass signals early exit

    const finalCost = this.applyFactors(sensorRow, baseCost);

    sensorRow.selectedCost = finalCost;
    sensorRow.unitcost = finalCost;

    // Apply user-specific cost multiplier if present.
    if (sensorRow.userCost > 0 && sensorRow.selectedCost > 0) {
      sensorRow.selectedCost = sensorRow.selectedCost * sensorRow.userCost;
    }
  }

  /**
   * Ensure ONAFactor, holdbackFactor, cloudFactor default to 1 if undefined.
   */
  protected ensureFactorDefaults(sensorRow: any): void {
    if (sensorRow.ONAFactor === undefined) sensorRow.ONAFactor = 1;
    if (sensorRow.holdbackFactor === undefined) sensorRow.holdbackFactor = 1;
    if (sensorRow.cloudFactor === undefined) sensorRow.cloudFactor = 1;
  }

  /**
   * Apply holdback, cloud, and ONA factors to base cost.
   * Legacy formula: total = base + base*(holdback-1) + base*(cloud-1) + base*(ONA-1)
   */
  protected applyFactors(sensorRow: any, baseCost: number): number {
    const holdbackFactor = Number(sensorRow.holdbackFactor) - 1;
    const cloudFactor = Number(sensorRow.cloudFactor) - 1;
    const ONAFactor = Number(sensorRow.ONAFactor) - 1;

    const holdbackValue = baseCost * holdbackFactor;
    const ccValue = baseCost * cloudFactor;
    const onaValue = baseCost * ONAFactor;

    return +baseCost + +holdbackValue + +ccValue + +onaValue;
  }

  /**
   * Default: no resolution constraints. Subclasses may override.
   */
  applyResolutionConstraints(_sensorRow: any, _productMode: any): boolean {
    return false;
  }
}
